import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetMyLanguageQuery,
  useGetLanguageStringsQuery,
  useUpdateMyLanguageMutation,
} from "../redux/admin/userApi";

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const authUser = useSelector((state) => state.auth?.user || null);
  const { data: myLang, isLoading: myLangLoading } = useGetMyLanguageQuery();
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("app_lang");
    const userLanguage = authUser?.language;
    return stored || userLanguage || "en";
  });

  // sync with backend saved lang
  useEffect(() => {
    if (myLang) setLang(myLang);
  }, [myLang]);

  useEffect(() => {
    if (authUser?.language) {
      setLang(authUser.language);
    }
  }, [authUser?.language]);

  useEffect(() => {
    document.documentElement.lang = lang || "en";
    localStorage.setItem("app_lang", lang || "en");
  }, [lang]);

  const {
    data: strings = {},
    isLoading: stringsLoading,
    refetch: refetchStrings,
  } = useGetLanguageStringsQuery(lang, { skip: !lang });

  const [updateMyLanguage, { isLoading: saving }] = useUpdateMyLanguageMutation();

  const t = useMemo(() => {
    return (key, fallback) => {
      if (!key) return "";
      return strings?.[key] ?? fallback ?? key;
    };
  }, [strings]);

  const changeLanguage = async (newLang) => {
    setLang(newLang); // ✅ instant UI change attempt (optimistic)
    try {
      await updateMyLanguage(newLang).unwrap(); // ✅ save on backend
      refetchStrings(); // ✅ reload strings
      const rawUser = localStorage.getItem("user");
      if (rawUser) {
        try {
          const nextUser = { ...JSON.parse(rawUser), language: newLang };
          localStorage.setItem("user", JSON.stringify(nextUser));
        } catch (error) {
          console.warn("[LANG] Failed to sync stored user language", error);
        }
      }
    } catch (e) {
      // fallback if save fails: restore old
      setLang(myLang || authUser?.language || "en");
    }
  };

  const value = useMemo(
    () => ({
      lang,
      t,
      changeLanguage,
      loading: myLangLoading || stringsLoading,
      saving,
    }),
    [lang, t, changeLanguage, myLangLoading, stringsLoading, saving]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider />");
  return ctx;
};
